// Practice Project Card Component
// Date: January 21, 2026

import { Card, CardContent, CardFooter, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { BookOpen, Clock, Users, FileText } from "lucide-react";
import { PracticeProject } from "../../types/practiceProjects";
import {
  getDifficultyColor,
  getStatusColor,
  formatDifficulty,
  formatStatus,
  getEstimatedTime,
  truncateText,
  htmlToPlainText,
} from "../../utils/practiceProjects.utils";
import paths from "../../routes";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { ROLES } from "../../constant/roles";

interface PracticeProjectCardProps {
  project: PracticeProject;
  variant?: "browse" | "management";
  onEdit?: (projectId: string) => void;
  onDelete?: (projectId: string) => void;
  onPublish?: (projectId: string) => void;
  onEnroll?: (projectId: string) => void;
  showActions?: boolean;
}

export function PracticeProjectCard({
  project,
  variant = "browse",
  onEdit,
  onDelete,
  onPublish,
  onEnroll,
  showActions = true,
}: PracticeProjectCardProps) {
  const navigate = useNavigate();

  const { user } = useAuth();
  const isAdmin =
    user?.role === ROLES.ADMIN ||
    user?.role === ROLES.INSTRUCTOR ||
    user?.role === ROLES.TA;

  const handleCardClick = () => {
    if (variant === "browse" && !isAdmin && project.status !== "published") {
      toast.error("The project is yet to be published, Contact your Admin");
      return;
    }
    navigate(paths.toPracticeProjectDetails(project.id));
  };

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const description = htmlToPlainText(project.description);
  const isEnrolled = project.enrollmentStatus?.isEnrolled || false;
  const completionPercentage =
    project.enrollmentStatus?.completionPercentage || 0;
  const displayTitle =
    project.title && project.title.length > 25
      ? `${project.title.slice(0, 25)}...`
      : project.title;

  return (
    <Card
      className="hover:shadow-lg transition-shadow cursor-pointer h-full flex flex-col"
      onClick={handleCardClick}
    >
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex-1 pr-6">
            <Tooltip>
              <TooltipTrigger asChild>
                <h3
                  className="font-semibold text-lg line-clamp-2 break-words cursor-pointer"
                  style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
                >
                  <p className="text-sm text-muted-foreground line-clamp-3 whitespace-normal">
                    {displayTitle}
                  </p>
                </h3>
              </TooltipTrigger>
              <TooltipContent
                className="max-w-md whitespace-pre-wrap break-words"
                style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
              >
                {project.title}
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex-shrink-0 ml-3">
            <Badge className={getDifficultyColor(project.difficulty_level)}>
              {formatDifficulty(project.difficulty_level)}
            </Badge>
          </div>
        </div>

        {variant === "management" && (
          <div className="flex gap-2">
            <Badge className={getStatusColor(project.status)}>
              {formatStatus(project.status)}
            </Badge>
            {project.course && (
              <Badge variant="outline" className="text-xs">
                {project.course.context_label}
              </Badge>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        {description && description.length > 100 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <p
                className="text-sm text-muted-foreground line-clamp-3 cursor-pointer break-words whitespace-pre-wrap"
                style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
              >
                {truncateText(description, 100)}
              </p>
            </TooltipTrigger>
            <TooltipContent
              className="max-w-md whitespace-pre-wrap break-words"
              style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
            >
              <div
                className="text-sm text-muted-foreground"
                style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
              >
                {description}
              </div>
            </TooltipContent>
          </Tooltip>
        ) : (
          <p className="text-sm text-muted-foreground line-clamp-3 break-words whitespace-pre-wrap">
            {description}
          </p>
        )}

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {project.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {project.tags.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{project.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{getEstimatedTime(project.difficulty_level)}</span>
          </div>

          {project.qaItems && project.qaItems.length > 0 && (
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              <span>{project.qaItems.length} Q&A</span>
            </div>
          )}

          {variant === "management" &&
            project.enrollmentCount !== undefined && (
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{project.enrollmentCount} enrolled</span>
              </div>
            )}
        </div>

        {/* Progress Bar for Enrolled Projects */}
        {isEnrolled && completionPercentage > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Progress</span>
              <span>{Math.round(completionPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        )}
      </CardContent>

      {showActions && (
        <CardFooter className="flex gap-2 pt-4">
          {variant === "browse" && (
            <>
              {!isEnrolled ? (
                <Button
                  className="flex-1"
                  onClick={(e) => handleAction(e, () => onEnroll?.(project.id))}
                  disabled={project.status !== "published"}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Enroll
                </Button>
              ) : (
                <Button
                  className="flex-1"
                  variant="default"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isAdmin && project.status !== "published") {
                      toast.error(
                        "The project is yet to be published, Contact your Admin",
                      );
                      return;
                    }
                    navigate(paths.toPracticeProjectDetails(project.id));
                  }}
                >
                  {completionPercentage === 100 ? "Review" : "Continue"}
                </Button>
              )}
            </>
          )}

          {variant === "management" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => handleAction(e, () => onEdit?.(project.id))}
              >
                Edit
              </Button>
              {project.status === "draft" && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={(e) =>
                    handleAction(e, () => onPublish?.(project.id))
                  }
                >
                  Publish
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={(e) => handleAction(e, () => onDelete?.(project.id))}
              >
                Delete
              </Button>
            </>
          )}
        </CardFooter>
      )}
    </Card>
  );
}
